import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Canvas, FabricData } from "../types/canvasTypes";
import { setCurrentCanvasData } from "@/store/templateEditor/templateEditorSlice";
import { 
  isHistoryProcessing, 
  setCustomProperties 
} from "../helpers/canvasCustomPropertiesHelper";
import { 
  triggerHistoryRestoredFromHistory,
  executeHistoryTransaction 
} from "../helpers/canvasHelpers";
import { 
  START_HISTORY_TRANSACTION_EVENT, 
  END_HISTORY_TRANSACTION_EVENT,
  CANVAS_INITIALIZED_EVENT 
} from "../constants/canvasConstants";
import { getCanvasJsonData } from "../utils/canvasUtils";
import { addWindowEventListener } from "@/lib/windowUtils";
import { isIgnoredEventOnInputTarget } from "../helpers/canvasHelpers";

interface UndoRedoState {
  canUndo: boolean;
  canRedo: boolean;
  historyLength: number;
  currentIndex: number;
}

interface UndoRedoActions {
  undo: () => void;
  redo: () => void;
  clearHistory: () => void;
  saveState: () => void;
}

interface UseUndoRedoOptions {
  maxHistorySize?: number;
  enableKeyboardShortcuts?: boolean;
  onStateChange?: (state: UndoRedoState) => void;
}

const DEFAULT_MAX_HISTORY_SIZE = 50;

export function useUndoRedo(
  canvas: Canvas | undefined,
  options: UseUndoRedoOptions = {}
): UndoRedoState & UndoRedoActions {
  const {
    maxHistorySize = DEFAULT_MAX_HISTORY_SIZE,
    enableKeyboardShortcuts = true,
    onStateChange,
  } = options;

  const dispatch = useDispatch();
  const historyStackRef = useRef<FabricData[]>([]);
  const currentIndexRef = useRef(-1);
  const isInTransactionRef = useRef(false);
  const pendingStateRef = useRef<FabricData | null>(null);
  const canvasInitializedRef = useRef(false);

  const [state, setState] = useState<UndoRedoState>({
    canUndo: false,
    canRedo: false,
    historyLength: 0,
    currentIndex: -1,
  });

  const updateState = useCallback(() => {
    const newState: UndoRedoState = {
      canUndo: currentIndexRef.current > 0,
      canRedo: currentIndexRef.current < historyStackRef.current.length - 1,
      historyLength: historyStackRef.current.length,
      currentIndex: currentIndexRef.current,
    };

    setState(newState);
    onStateChange?.(newState);
  }, [onStateChange]);

  const saveState = useCallback(() => {
    if (!canvas || !canvasInitializedRef.current) return;

    const canvasData = getCanvasJsonData(canvas);
    
    // Remove any states after current index (when we're in the middle of history)
    if (currentIndexRef.current < historyStackRef.current.length - 1) {
      historyStackRef.current = historyStackRef.current.slice(0, currentIndexRef.current + 1);
    }

    // Add new state
    historyStackRef.current.push(canvasData);
    currentIndexRef.current = historyStackRef.current.length - 1;

    // Limit history size
    if (historyStackRef.current.length > maxHistorySize) {
      historyStackRef.current.shift();
      currentIndexRef.current = Math.max(0, currentIndexRef.current - 1);
    }

    updateState();
  }, [canvas, maxHistorySize, updateState]);

  const undo = useCallback(() => {
    if (!canvas || currentIndexRef.current <= 0) return;

    const previousState = historyStackRef.current[currentIndexRef.current - 1];
    if (!previousState) return;

    executeHistoryTransaction(canvas, () => {
      setCustomProperties(canvas, { isHistoryProcessing: true });
      
      canvas.loadFromJSON(previousState, () => {
        canvas.renderAll();
        currentIndexRef.current = Math.max(0, currentIndexRef.current - 1);
        dispatch(setCurrentCanvasData(previousState));
        updateState();
        triggerHistoryRestoredFromHistory(canvas);
        setCustomProperties(canvas, { isHistoryProcessing: false });
      });
    });
  }, [canvas, dispatch, updateState]);

  const redo = useCallback(() => {
    if (!canvas || currentIndexRef.current >= historyStackRef.current.length - 1) return;

    const nextState = historyStackRef.current[currentIndexRef.current + 1];
    if (!nextState) return;

    executeHistoryTransaction(canvas, () => {
      setCustomProperties(canvas, { isHistoryProcessing: true });
      
      canvas.loadFromJSON(nextState, () => {
        canvas.renderAll();
        currentIndexRef.current = Math.min(historyStackRef.current.length - 1, currentIndexRef.current + 1);
        dispatch(setCurrentCanvasData(nextState));
        updateState();
        triggerHistoryRestoredFromHistory(canvas);
        setCustomProperties(canvas, { isHistoryProcessing: false });
      });
    });
  }, [canvas, dispatch, updateState]);

  const clearHistory = useCallback(() => {
    historyStackRef.current = [];
    currentIndexRef.current = -1;
    updateState();
  }, [updateState]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!enableKeyboardShortcuts) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isIgnoredEventOnInputTarget(event)) return;

      const isCmdOrCtrl = event.metaKey || event.ctrlKey;
      
      if (isCmdOrCtrl && event.key === "z") {
        event.preventDefault();
        if (event.shiftKey) {
          redo();
        } else {
          undo();
        }
      } else if (isCmdOrCtrl && event.key === "y") {
        event.preventDefault();
        redo();
      }
    };

    return addWindowEventListener("keydown", handleKeyDown);
  }, [enableKeyboardShortcuts, undo, redo]);

  // Handle canvas events
  useEffect(() => {
    if (!canvas) return;

    const handleCanvasInitialized = () => {
      canvasInitializedRef.current = true;
      
      // Save initial state
      if (historyStackRef.current.length === 0) {
        saveState();
      }
    };

    const handleStartTransaction = () => {
      isInTransactionRef.current = true;
    };

    const handleEndTransaction = () => {
      isInTransactionRef.current = false;
      
      // Save state if there's a pending state change
      if (pendingStateRef.current) {
        saveState();
        pendingStateRef.current = null;
      }
    };

    const handleObjectModified = () => {
      if (isHistoryProcessing(canvas)) return;

      if (isInTransactionRef.current) {
        // Store the state to be saved after transaction ends
        pendingStateRef.current = getCanvasJsonData(canvas);
      } else {
        // Save state immediately if not in transaction
        saveState();
      }
    };

    // Subscribe to canvas events
    canvas.on(CANVAS_INITIALIZED_EVENT, handleCanvasInitialized);
    canvas.on(START_HISTORY_TRANSACTION_EVENT, handleStartTransaction);
    canvas.on(END_HISTORY_TRANSACTION_EVENT, handleEndTransaction);
    canvas.on("object:modified", handleObjectModified);
    canvas.on("object:added", handleObjectModified);
    canvas.on("object:removed", handleObjectModified);

    return () => {
      canvas.off(CANVAS_INITIALIZED_EVENT, handleCanvasInitialized);
      canvas.off(START_HISTORY_TRANSACTION_EVENT, handleStartTransaction);
      canvas.off(END_HISTORY_TRANSACTION_EVENT, handleEndTransaction);
      canvas.off("object:modified", handleObjectModified);
      canvas.off("object:added", handleObjectModified);
      canvas.off("object:removed", handleObjectModified);
    };
  }, [canvas, saveState]);

  // Initialize state when canvas changes
  useEffect(() => {
    if (canvas) {
      canvasInitializedRef.current = false;
      clearHistory();
    }
  }, [canvas, clearHistory]);

  return {
    ...state,
    undo,
    redo,
    clearHistory,
    saveState,
  };
} 